import { useState } from "react";
import {
  Drawer, Button, Upload, Table, Tag, Select, Space, Alert, Steps,
} from "antd";
import { InboxOutlined, DownloadOutlined, DeleteOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { CorpClaimPortalService } from "../services/corpClaimPortalService";
import { notifyUser } from "../utils/notify";
import { DOCUMENT_TYPES } from "../constants/documentTypes";

const { Dragger } = Upload;

const TEMPLATE_HEADERS = [
  "Claim Type Code", "Scheme No", "Member Staff No",
  "Claimant Name", "Claimant Mobile", "Claimant Address",
  "Claimant DOB (YYYY-MM-DD)", "Event Date (YYYY-MM-DD)", "Claim Cause", "Benefit Amount",
];

const FIELD_MAP = {
  "Claim Type Code": "claim_type_code",
  "Scheme No": "scheme_no",
  "Member Staff No": "member_staff_no",
  "Claimant Name": "claimant_name",
  "Claimant Mobile": "claimant_mobile",
  "Claimant Address": "claimant_address",
  "Claimant DOB (YYYY-MM-DD)": "claimant_date_of_birth",
  "Event Date (YYYY-MM-DD)": "event_date",
  "Claim Cause": "claim_cause",
  "Benefit Amount": "benefit_amount",
};

const downloadTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([
    TEMPLATE_HEADERS,
    [
      "DTH", "GLA/2018/00706", "STF001",
      "Jane Doe", "0244000000", "123 Main St, Accra",
      "1990-01-01", "2026-09-01", "Illness", "5000",
    ],
  ]);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Claims");
  XLSX.writeFile(wb, "Corp_Claims_Bulk_Template.xlsx");
};

/**
 * Excel bulk upload for HR/Broker — every row is submitted under the
 * logged-in portal user (unlike the staff-side CRM bulk upload, there's no
 * HR/Broker column to resolve since the caller IS the HR/Broker). Parses the
 * template client-side, lets the user tag staged documents to a specific row
 * before anything is submitted, then submits the whole batch in one call.
 */
const BulkUploadDrawer = ({ open, onClose, onSubmitted }) => {
  const [rows, setRows] = useState([]);
  const [stagedDocs, setStagedDocs] = useState([]);
  const [docRowRef, setDocRowRef] = useState();
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState(DOCUMENT_TYPES[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const reset = () => {
    setRows([]);
    setStagedDocs([]);
    setResults(null);
  };

  const handleParse = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const parsed = raw.map((r, i) => {
          const row = { row_ref: String(i + 1) };
          Object.entries(FIELD_MAP).forEach(([header, field]) => {
            row[field] = String(r[header] ?? "").trim();
          });

          const missing = [];
          if (!row.claim_type_code) missing.push("Claim Type Code");
          if (!row.scheme_no) missing.push("Scheme No");
          if (!row.claimant_name) missing.push("Claimant Name");
          if (!row.event_date) missing.push("Event Date");
          if (!row.claim_cause) missing.push("Claim Cause");
          row._problems = missing;

          return row;
        });

        setRows(parsed);
      } catch {
        notifyUser("error", "Could not read that file — make sure it's a filled copy of the template.", "Error");
      }
    };
    reader.readAsBinaryString(file);
    return false;
  };

  const addStagedDoc = () => {
    if (!docRowRef || !docFile) return notifyUser("error", "Select a row and a file first.", "Missing info");
    setStagedDocs([...stagedDocs, { row_ref: docRowRef, file: docFile, name: docFile.name, type: docType }]);
    setDocFile(null);
  };

  const handleSubmit = async () => {
    const validRows = rows.filter((r) => r._problems.length === 0);
    if (!validRows.length) return notifyUser("error", "No valid rows to submit.", "Nothing to submit");

    setSubmitting(true);
    try {
      const claims = validRows.map((r) => ({
        row_ref: r.row_ref,
        claim_type_code: r.claim_type_code,
        scheme_no: r.scheme_no,
        member_staff_no: r.member_staff_no || undefined,
        claimant_name: r.claimant_name,
        claimant_mobile: r.claimant_mobile || undefined,
        claimant_address: r.claimant_address || undefined,
        claimant_date_of_birth: r.claimant_date_of_birth || undefined,
        event_date: r.event_date,
        claim_cause: r.claim_cause,
        benefit_amount: r.benefit_amount || undefined,
      }));

      const res = await CorpClaimPortalService.bulkSubmit({ claims });
      const rowResults = res?.data || [];

      let docFailures = 0;
      for (const result of rowResults) {
        if (result.status !== "created") continue;
        const docsForRow = stagedDocs.filter((d) => d.row_ref === result.row_ref);
        for (const doc of docsForRow) {
          try {
            const fd = new FormData();
            fd.append("file", doc.file);
            fd.append("name", doc.name);
            fd.append("type", doc.type);
            await CorpClaimPortalService.uploadDocument(result.claim_id, fd);
          } catch {
            docFailures += 1;
          }
        }
      }

      setResults({ rowResults, docFailures });
      onSubmitted?.();
    } catch {
      notifyUser("error", "The batch could not be submitted. Please try again.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const created = results?.rowResults?.filter((r) => r.status === "created").length || 0;
  const failed = results?.rowResults?.filter((r) => r.status === "failed").length || 0;

  return (
    <Drawer
      title="Bulk Upload Claims"
      open={open}
      onClose={() => { onClose(); reset(); }}
      width={window.innerWidth > 900 ? 850 : "100%"}
      destroyOnClose
    >
      <Steps
        size="small"
        className="mb-4"
        current={results ? 2 : rows.length ? 1 : 0}
        items={[{ title: "Upload Excel" }, { title: "Attach Documents & Review" }, { title: "Done" }]}
      />

      {results ? (
        <div>
          <Alert
            type={failed ? "warning" : "success"}
            showIcon
            className="mb-3"
            message={`${created} of ${results.rowResults.length} claims created${results.docFailures ? `, ${results.docFailures} document(s) failed to attach` : ""}`}
          />
          <Table
            size="small"
            rowKey="row_ref"
            dataSource={results.rowResults}
            pagination={false}
            columns={[
              { title: "Row", dataIndex: "row" },
              { title: "Status", dataIndex: "status", render: (v) => <Tag color={v === "created" ? "green" : "red"}>{v}</Tag> },
              { title: "Errors", dataIndex: "errors", render: (v) => v?.join("; ") || "—" },
            ]}
          />
          <Button type="primary" className="mt-3" onClick={() => { onClose(); reset(); }} style={{ background: "#189F6B", borderColor: "#189F6B" }}>
            Close
          </Button>
        </div>
      ) : !rows.length ? (
        <div>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message="Download the template, fill in one row per claim, then upload it here."
          />
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate} className="mb-3">
            Download Excel Template
          </Button>
          <Dragger beforeUpload={handleParse} maxCount={1} accept=".xlsx,.xls" showUploadList={false}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the filled template here</p>
          </Dragger>
        </div>
      ) : (
        <div>
          <Table
            size="small"
            rowKey="row_ref"
            dataSource={rows}
            pagination={false}
            scroll={{ y: 300 }}
            className="mb-3"
            columns={[
              { title: "Row", dataIndex: "row_ref", width: 50 },
              { title: "Claimant", dataIndex: "claimant_name" },
              { title: "Type", dataIndex: "claim_type_code" },
              { title: "Scheme", dataIndex: "scheme_no" },
              {
                title: "Status",
                render: (_, r) => r._problems.length
                  ? <Tag color="red">{r._problems.join(", ")}</Tag>
                  : <Tag color="green">Ready</Tag>,
              },
            ]}
          />

          <Alert type="info" showIcon className="mb-3" message="Optionally attach documents to specific rows before submitting" />
          <Space wrap className="mb-2">
            <Select
              style={{ width: 160 }}
              placeholder="Row"
              value={docRowRef}
              onChange={setDocRowRef}
              options={rows.map((r) => ({ value: r.row_ref, label: `Row ${r.row_ref} — ${r.claimant_name || "?"}` }))}
            />
            <Select style={{ width: 170 }} value={docType} onChange={setDocType} options={DOCUMENT_TYPES} />
            <Upload beforeUpload={(file) => { setDocFile(file); return false; }} maxCount={1} showUploadList={false}>
              <Button icon={<FileExcelOutlined />}>{docFile ? docFile.name : "Choose file"}</Button>
            </Upload>
            <Button onClick={addStagedDoc}>Attach to row</Button>
          </Space>

          {stagedDocs.length > 0 && (
            <Space direction="vertical" style={{ width: "100%" }} className="mb-3">
              {stagedDocs.map((d, i) => (
                <div key={i} className="d-flex align-items-center justify-content-between border rounded p-2">
                  <span>Row {d.row_ref}: {d.name}</span>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setStagedDocs(stagedDocs.filter((_, idx) => idx !== i))} />
                </div>
              ))}
            </Space>
          )}

          <Space>
            <Button onClick={() => setRows([])}>Start Over</Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={!rows.some((r) => r._problems.length === 0)}
              style={{ background: "#189F6B", borderColor: "#189F6B" }}
              onClick={handleSubmit}
            >
              Submit {rows.filter((r) => r._problems.length === 0).length} Claim(s)
            </Button>
          </Space>
        </div>
      )}
    </Drawer>
  );
};

export default BulkUploadDrawer;
